import {useEffect, useState} from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {axiosFunction, toastMessage} from "../../../components/commonFunctions";
import {v2ReactURL} from "../../../utils/constant.ts";
import {useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import useResponsive from "../../../hooks/useResponsive.ts";
import ErrorPage from "../../Error/ErrorPage.tsx";
import Loader from "../../../utils/Loader.tsx";
import {Column, Row, Wrapper, WrapperCard, WrapperContent, WrapperSubHeader} from "../../../components/common";
import BreadCrumbComponent from "../../../components/common/BreadCrumbComponent/BreadCrumbComponent.tsx";
import ContactFormAccordian from "../../../components/common/Accordian/ContactFormAccordian.tsx";
import Footer from "../../../layout/footer/Footer.tsx";
import {ErrorState} from "../../Contact/AddContact/AddContact.tsx";
import LoyaltyGeneralDetailsFormComponent
    from "../../../components/common/FormComponent/VLoyalty/LoyaltyGeneralDetailsFormComponent.tsx";
import {
    CampaignApiResponseSchema,
    CampaignSchema, CampaignForm, campaignSchema
} from "../EditCampaign/EditCampaignSchema.ts";
import LoyaltyParameterDetailsFormComponent
    from "../../../components/common/FormComponent/VLoyalty/LoyaltyParameterDetailsComponent.tsx";
import parameterDetailsIcon from "../../../assets/images/main/parameterDetailsIcon.svg"
import {apiResponseHandler} from "../../../utils/functions/apiResponseHandler.ts";
import {DateFormat} from "../../../utils/DateFormat.ts";
import {useSelector} from "react-redux";
import { useToggleContext } from '../../../layout/ToggleContext.tsx';

const EditCampaign = () => {
    const decimalPoint = useSelector((state: any) => state.sessionData.sessionScope.decimalPoint);
    const navigate = useNavigate();
    const isMobile = useResponsive();
    const params = useParams();
    const { isToggle } = useToggleContext();
    const [error, setError] = useState<ErrorState | null>(null);
    const [isLoading, setLoading] = useState<boolean>(true);
    const [isLoadingBtn, setIsLoadingBtn] = useState(false);
    const [newCampaignDetail, setNewCampaignDetail] = useState<CampaignSchema | null>(null);
    const [isPercentage, setIsPercentage] = useState<boolean>(false);
    const [showDiscountField, setShowDiscountField] = useState<boolean>(true);


    useEffect(() => {
        const fetchNewCampaignDetail = async () => {
            setIsLoadingBtn(true);
            const response = await axiosFunction(
                `${v2ReactURL}/loyalty/campaign/${params.campaignId}/edit`,
                "GET"
            );

            const error: ErrorState | null = apiResponseHandler(response, false);
            if (error != null) {
                setError(error);
                setIsLoadingBtn(false);
                return;
            }

            const parsedResult = CampaignApiResponseSchema.safeParse(response.data);
            if (parsedResult.success) {
                setNewCampaignDetail(parsedResult.data.response);
                setIsPercentage(parsedResult.data.response?.campaign?.discountInPercentage || false);
            } else {
                console.error("Parsing error:", parsedResult.error.format());
            }
            setLoading(false);
            setIsLoadingBtn(false);
            setError(null);
        };
        fetchNewCampaignDetail();
    }, []);

    const {
        register,
        handleSubmit,
        formState: {errors},
        setValue,
        control,
        watch
    } = useForm<CampaignForm>({
        defaultValues: {
            name: null,
            points: null,
            discount: null,
            description: null,
            launchDate: new Date().toISOString().split("T")[0],
            expiryDate: null,
            minPurchaseAmount: newCampaignDetail?.campaign?.minPurchaseAmount || 0,
        },
        resolver: yupResolver(campaignSchema as any),
        mode: "onChange",
    });

    useEffect(() => {
        setValue("name", newCampaignDetail?.campaign?.name || null);
        setValue("points", parseFloat(newCampaignDetail?.campaign?.points?.toFixed(decimalPoint || 2) ?? "0"));
        setValue("discount", parseFloat(newCampaignDetail?.campaign?.discount?.toFixed(decimalPoint || 2) || "") || null);
        setValue("description", newCampaignDetail?.campaign?.description || null);
        // Format the date to "YYYY-MM-DD"
        setValue(
            "launchDate",
            newCampaignDetail?.campaign?.launchDate
                ? new Date(newCampaignDetail.campaign.launchDate).toISOString().split("T")[0]
                : ""
        );


        setValue(
            "expiryDate",
            newCampaignDetail?.campaign?.expiryDate
                ? new Date(newCampaignDetail.campaign.expiryDate).toISOString().split("T")[0]
                : ""
        );
        setValue("minPurchaseAmount", parseFloat(newCampaignDetail?.campaign?.minPurchaseAmount?.toFixed(decimalPoint || 2) || "") || 0);
        setValue("type", {
            value: newCampaignDetail?.campaign?.type || "",
            label: newCampaignDetail?.campaign?.type || ""
        });
        setValue("redemptionType", {
            value: newCampaignDetail?.campaign?.redemptionType || "",
            label: newCampaignDetail?.campaign?.redemptionType || ""
        });
    }, [newCampaignDetail]);

    const payloadData = (data: CampaignForm) => {
        return {
            loyaltyCampaignId: params.campaignId,
            name: data.name,
            type: data.type.value,
            points: data.points,
            description: data.description,
            discount: data.type.value === "Discount" ? data.discount : 0,
            redemptionType: data.redemptionType.value,
            minPurchaseAmount: data.minPurchaseAmount,
            launchDate: data.launchDate && DateFormat(data.launchDate),
            expiryDate: data.expiryDate && DateFormat(data.expiryDate),
            discountInPercentage: isPercentage,
        };
    };

    const saveCampaign = async (data: CampaignForm) => {
        if (data.launchDate && data.expiryDate) {
            const launchDate = new Date(data.launchDate);
            const expiryDate = new Date(data.expiryDate);

            if (expiryDate < launchDate) {
                toastMessage("Expiry date should be after the launch date.");
                return;
            }
        }

        const response = await axiosFunction(
            `${v2ReactURL}/loyalty/campaign/save`,
            "POST",
            payloadData(data)
        );

        const error: ErrorState | null = apiResponseHandler(response, true);
        if (error != null) {
            toastMessage(error.message);
            return;
        }
        return response;
    }

    const onSave = async (data: CampaignForm) => {
        const response = await saveCampaign(data)
        if (response) {
            navigate(`/loyalty/campaign/${response.data.response}`);
        }
    };

    if (error) {
        return <ErrorPage error={error}/>;
    }

    if (isLoading) {
        return <Loader/>;
    }

    return <Wrapper
        navUrl=""
        customStyle={{height: "auto"}}
        subClassName={"addContactWrapper"}
    >
        <WrapperSubHeader>
            <Column md={8}></Column>
            <Column md={2}></Column>
            <Column md={2} className="pl-md-0 mt-1 mt-md-0"></Column>
        </WrapperSubHeader>
        <WrapperContent>
            <BreadCrumbComponent
                type={`Campaign`}
                subTypeClickable={false}
                typeLink="/loyalty"
            />
            <Row>
                <WrapperCard>
                    <ContactFormAccordian
                        generalDetailsForm={
                            <LoyaltyGeneralDetailsFormComponent
                                isPercentage={isPercentage}
                                setIsPercentage={setIsPercentage}
                                campaignDetails={newCampaignDetail}
                                register={register}
                                errors={errors}
                                control={control}
                                setValue={setValue}
                                watch={watch}
                                showDiscountField={showDiscountField}
                                setShowDiscountField={setShowDiscountField}
                            />
                        }
                        addressDetailsForm={
                            <LoyaltyParameterDetailsFormComponent
                                setValue={setValue}
                                register={register}
                                errors={errors}
                                control={control}
                                watch={watch}
                                showDiscountField={showDiscountField}
                                setShowDiscountField={setShowDiscountField}
                            />
                        }
                        secondCollapseTitle="Parameter Details"
                        secondCollapseIcon={parameterDetailsIcon}
                    />
                </WrapperCard>
            </Row>
        </WrapperContent>
        <div
            style={{
                position: "fixed",
                bottom: 0,
                width: `calc(100% - ${isToggle ? 78 : 230}px)`,
                zIndex: 999,
                left: isMobile ? 0 : isToggle ? 78 : 230
            }}
        >
            <Footer
                onSubmit={handleSubmit(onSave)}
                clearBtn={false}
                handleClear={() => {
                }}
                addressDetailsError={false}
                onCancelClick={() => {
                    navigate("/loyalty");
                }}
                isLoading={isLoadingBtn}
            />
        </div>
    </Wrapper>
};

export default EditCampaign;
